/**
 * Low-level HTTP request primitive for dink-admin (Option A).
 *
 * The transport is the **vendored fleet axios factory** (`./httpClientFactory`,
 * from `software-architect-ai/templates/api-axios-instance.ts`) configured via
 * `createApiClient`. The four interceptors (base+headers / bearer / csrf /
 * silent-refresh+error-normalize) are centralized there; this module supplies
 * dink-admin's config and a THIN request-config wrapper for the extras the
 * factory does not emit. Interceptor changes come from RE-VENDORING the fleet
 * template, never from editing it locally.
 *
 * Two surfaces, strictly separated:
 *
 *  - saas-auth COOKIE surface (`/api/auth/**`, e.g. refresh / logout):
 *      base = `getBootstrap().authOrigin` (a platform singleton this app
 *      cannot compute — read verbatim from the resolved bootstrap,
 *      DNKADM-0274); `credentials:'include'`; CSRF double-submit
 *      (header `X-CSRF-Token` + body `csrfToken`, cookie-echo mode) on
 *      state-mutating methods only. NO bearer token.
 *
 *  - dink-api BEARER surface (`/admin/**`, `/me/**`, `/v2/**`):
 *      base = `env.journixApiBaseUrl`; `Authorization: Bearer <token>`; NO
 *      cookies, NO CSRF — it is a stateless OAuth2 resource server. We
 *      deliberately DO NOT port saas-admin's `/api/saas-admin/**`
 *      admin-mutation CSRF branch; it does not apply here. `/me/**`
 *      (DNKADM-0031) and `/v2/**` (DNKADM-0049) are the SAME bearer surface as
 *      `/admin/**` — bearer only, no CSRF, no cookies, the same bounded
 *      401→refresh→retry. (The v1 `/calendars/**` surface was retired in
 *      DNKADM-0099 once `getServiceLocations` moved to `/v2/admin/locations`.)
 *
 * Every call carries `X-Request-ID`. A `401` on a bearer call triggers ONE
 * silent `POST /api/auth/refresh` and ONE retry — bounded, never looping (the
 * factory's single-in-flight refresh + `_retry` guard guarantee it).
 */
import axios, {
  AxiosError,
  getAdapter,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { createApiClient, ApiError as FactoryApiError } from './httpClientFactory';
import { ApiError, statusToCode, type ErrorCode } from './errors';
import { generateRequestId } from '@/utils/uuid';
import { env } from '@/config/env';
import { getBootstrap } from '@/config/bootstrap';
import {
  getAccessToken,
  getClaims,
  getExpiresAt,
  getSignOutGeneration,
  setAccessToken,
  getCsrfToken,
  setCsrfToken,
  signOut,
} from '@/auth/authStore';
import { readFrontDeskView } from '@/auth/frontDeskContextValue';
import {
  browserRefreshChannel,
  browserRefreshLocks,
  createRefreshCoordinator,
  type RefreshResult,
} from '@/auth/refreshCoordinator';
import { decodeJwtPayload, tenantSlugFromClaims } from '@/utils/jwt';

/** saas-auth cookie + CSRF surface. */
function isAuthSurface(path: string): boolean {
  return path.startsWith('/api/auth/');
}

/** dink-api admin bearer-only surface. */
function isAdminSurface(path: string): boolean {
  return path.startsWith('/admin/');
}

/** dink-api `/me/**` bearer-only surface (e.g. `/me/verify-password`). */
function isMeSurface(path: string): boolean {
  return path.startsWith('/me/');
}

/**
 * dink-api `/v2/**` bearer-only surface — the Project Independence (v2) rebuild
 * (DNKADM-0049). v2 admin endpoints are `/v2/admin/**` (member/public under
 * `/v2/me/**`, `/v2/public/**`); the whole `/v2/**` tree is the SAME bearer
 * surface as `/admin/**` — bearer token, NO CSRF, NO cookies, the same bounded
 * 401→refresh→retry. Purely routing; the CSRF/cookie/refresh invariants are
 * unchanged.
 */
function isV2Surface(path: string): boolean {
  return path.startsWith('/v2/');
}

/**
 * dink-api `/v2/admin/**` mutating-audit surface (DNKADM-0049). The mutating
 * `X-Session-Mode` stamp fires on `/v2/admin/**` exactly as it does on the v1
 * `/admin/**` surface (handoff §3) — same audit-attribution semantics.
 */
function isV2AdminSurface(path: string): boolean {
  return path.startsWith('/v2/admin/');
}

/**
 * dink-api contact-photo serve path (`GET /members/{contactId}/photo` — the
 * DNKAPI-0104 white-label serve route, the exact string the detail read's
 * `photoUrl` carries). It is JWT-required server-side (verified: dink-api's
 * `ContactPhotoController.servePhoto` reads the principal for tenant scoping),
 * so a plain `<img src>` gets a 401 — DNKADM-0304 U4 fetches it through the
 * authed client instead, which needs this ONE narrow path on the bearer
 * surface. Deliberately anchored (`/members/{id}/photo` only), not a blanket
 * `/members/**` widening.
 */
function isMemberPhotoServePath(path: string): boolean {
  return /^\/members\/[^/]+\/photo$/.test(path);
}

/**
 * The dink-api BEARER surface: `/admin/**`, `/me/**`, plus `/v2/**`. All carry the
 * bearer token, NO CSRF and NO cookies, and share the bounded 401→refresh→retry.
 * The `/me/**` addition (DNKADM-0031) and the `/v2/**` addition (DNKADM-0049 — the
 * Project Independence rebuild) are purely routing — they do NOT change the
 * CSRF/cookie/refresh behavior. (The v1 `/calendars/**` surface, DNKADM-0030, was
 * retired in DNKADM-0099 when `getServiceLocations` moved to `/v2/admin/locations`
 * — its last caller.) DNKADM-0304 adds the single anchored contact-photo serve
 * path — same bearer-only treatment, same routing, nothing else under
 * `/members/**`.
 */
function isBearerSurface(path: string): boolean {
  return (
    isAdminSurface(path) || isMeSurface(path) || isV2Surface(path) || isMemberPhotoServePath(path)
  );
}

/**
 * Two-base routing: bearer surface → dink-api (the one computed origin);
 * everything else → saas-auth, whose origin is a platform singleton this app
 * cannot compute and instead reads verbatim from the resolved bootstrap
 * (DNKADM-0274). `main.tsx`'s `BootstrapGate` guarantees bootstrap has
 * resolved before the router — and so any request — can render.
 */
function baseUrlFor(path: string): string {
  if (isBearerSurface(path)) return env.journixApiBaseUrl;
  return getBootstrap().authOrigin;
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** saas-auth silent-refresh endpoint (cookie surface). */
const REFRESH_PATH = '/api/auth/refresh';
/** saas-auth CSRF issue endpoint (the factory's default `csrf.fetchPath`). */
const CSRF_PATH = '/api/auth/csrf';
/** The factory's default cookie-echo cookie name. */
const CSRF_COOKIE = 'journix_csrf';

/**
 * DNKADM-0497 bounds — nothing on the refresh path may WAIT forever, but a slow
 * refresh is never cut off: today a slow refresh eventually succeeds, and aborting
 * it would turn slow iPad wifi into a sign-out. None of these bounds signs out.
 *  - `lockWaitMs`: how long a refresh waits for the lock before going out
 *    unlocked (as before the lock existed).
 *  - `lockHoldMs`: how long a refresh holds the lock. Past it the lock is released
 *    so other tabs can proceed (their SUPERSEDED retry covers any overlap), while
 *    this tab's request keeps running and its result is still used.
 *  - `wakeWaitMs`: how long after a wake refresh starts dink-api calls wait for it;
 *    after that they go out with the token they have.
 */
const DEFAULT_REFRESH_BOUNDS = { lockWaitMs: 10_000, lockHoldMs: 10_000, wakeWaitMs: 10_000 };
let refreshBounds = { ...DEFAULT_REFRESH_BOUNDS };

/** Test seam — shorten the DNKADM-0497 bounds; `null` restores the defaults. */
export function __setRefreshBoundsForTest(bounds: Partial<typeof DEFAULT_REFRESH_BOUNDS> | null): void {
  refreshBounds = { ...DEFAULT_REFRESH_BOUNDS, ...(bounds ?? {}) };
}

/**
 * With Web Locks, the coordinated refresh gets its CSRF token INSIDE the lock
 * (`resendRefresh`), so the factory's cookie-echo stage must not run for it: that
 * stage runs before the adapter — before the lock — and its `GET /api/auth/csrf`
 * makes saas-auth issue a NEW cookie, which would overwrite the token another tab
 * is about to send from inside the lock (403 INVALID_CSRF → "Session expired").
 * Without Web Locks the refresh keeps the factory's CSRF, exactly as today.
 */
function coordinatorOwnsRefreshCsrf(path: string): boolean {
  return path === REFRESH_PATH && browserRefreshLocks() !== null;
}

/**
 * Cross-tab refresh coordination (DNKADM-0497) — Web Lock + same-user reuse of
 * another tab's fresh result + one retry on `REFRESH_TOKEN_SUPERSEDED`. See
 * `@/auth/refreshCoordinator`. Another tab's result is reused only when it is for
 * the SAME signed-in user and tenant this tab already holds, so a cold boot or a
 * post-login return (no token in memory yet) always refreshes for itself and can
 * never pick up a different session's token.
 */
const refreshCoordinator = createRefreshCoordinator({
  locks: browserRefreshLocks,
  channel: browserRefreshChannel,
  canReuse: (result: RefreshResult) => {
    const current = getClaims();
    const next = decodeJwtPayload(result.accessToken);
    if (!current?.sub || !next?.sub || current.sub !== next.sub) return false;
    return tenantSlugFromClaims(current) === tenantSlugFromClaims(next);
  },
  lockWaitMs: () => refreshBounds.lockWaitMs,
  lockHoldMs: () => refreshBounds.lockHoldMs,
});

/**
 * The single shared axios instance, built from the vendored fleet factory with
 * dink-admin's Option-A config:
 *  - resolveBaseUrl = the two-base `baseUrlFor` routing.
 *  - tokens = the in-memory `authStore` (the factory NEVER touches storage).
 *  - bearer.protected = `isBearerSurface` (`/admin`,`/me`,`/v2`).
 *  - csrf.protected = mutating `/api/auth/**` only; mode = `cookie-echo`
 *    (`X-CSRF-Token` header + `csrfToken` body echo, cookie-first with a
 *    `GET /api/auth/csrf` fallback). dink-api surfaces get NO CSRF, NO cookies.
 *  - refresh.path = `/api/auth/refresh`; onSessionExpired = `signOut({ expired: true })`
 *    (the ONLY runtime session-expiry source — it flags the sign-out as an expiry
 *    so React renders the "Session expired" interstitial; explicit logout calls
 *    bare `signOut()`).
 *  - generateRequestId = the existing UUID generator (`X-Request-ID`).
 *  - getTenantContext OMITTED — tenancy is server-derived (no `X-Tenant-ID`).
 */
const instance: AxiosInstance = createApiClient({
  resolveBaseUrl: baseUrlFor,
  tokens: {
    getAccess: getAccessToken,
    // The refresh body's `expiresAt` is an ISO string (saas-admin shape); the
    // factory types it loosely — forward it through to the store unchanged.
    setAccess: (token, expiresAt) =>
      setAccessToken(token, (expiresAt as string | null | undefined) ?? null),
    getCsrf: getCsrfToken,
    setCsrf: setCsrfToken,
  },
  bearer: { protected: isBearerSurface },
  csrf: {
    // CSRF double-submit applies ONLY to the saas-auth cookie surface, on
    // state-mutating methods. dink-api never gets a CSRF header. DNKADM-0497: with
    // Web Locks, the refresh POST's CSRF is set inside the lock by `resendRefresh`
    // instead (see `coordinatorOwnsRefreshCsrf`).
    protected: (path, method) =>
      isAuthSurface(path) && MUTATING.has(method) && !coordinatorOwnsRefreshCsrf(path),
    mode: () => 'cookie-echo',
  },
  refresh: { path: REFRESH_PATH },
  onSessionExpired: () => {
    refreshCoordinator.clear();
    // DNKADM-0497: a refresh that fails because this tab was explicitly logged out
    // meanwhile must not raise the "Session expired" interstitial over that logout.
    if (getAccessToken() === null) return;
    signOut({ expired: true });
  },
  generateRequestId,
});

/**
 * dink-admin parity guard, registered HERE (not in the vendored factory) so the
 * factory stays canonical. Two concerns, both keyed off the factory's `_retry`
 * marker:
 *
 *  1. Scope the silent 401→refresh→retry to the BEARER surface only. The factory
 *     treats ANY 401 as refreshable; dink-admin refreshes only on the dink-api
 *     bearer surface (the cookie surface — refresh / logout / csrf — either
 *     succeeds or is terminally unauthenticated, and the refresh POST must never
 *     recurse into another refresh). Pre-marking every NON-bearer request as
 *     `_retry` makes the factory's `!_retry` gate skip them, so a 401 on the auth
 *     surface (including the refresh POST and the CSRF GET themselves) is
 *     normalized, never refreshed — exactly the prior `isBearerSurface`-scoped,
 *     `_isRefresh`/`isRefreshPath`-guarded behavior.
 *
 *  2. Strip `X-Session-Mode` on the post-refresh retry. The factory marks the
 *     retried bearer config with `_retry` and replays it verbatim; the
 *     audit-attribution header is excluded from the retry (DNKADM-0034 treats the
 *     retry as an internal call), so the replay must not re-emit it.
 */
instance.interceptors.request.use((req) => {
  const r = req as { _retry?: boolean };
  if (!isBearerSurface(pathOf(req)) && r._retry === undefined) {
    r._retry = true; // auth-surface + internal calls never trigger a refresh
  }
  if (r._retry) {
    req.headers.delete('X-Session-Mode');
  }
  return req;
});

/** Marks the coordinator's own SUPERSEDED retry so it is sent raw (it already runs inside the lock). */
type RefreshRetryConfig = AxiosRequestConfig & { _dinkRefreshRetry?: true };

/**
 * DNKADM-0497 — route EVERY refresh through the coordinator. Both refresh paths
 * POST `/api/auth/refresh` through this instance: the vendored factory's
 * `runRefresh` (401 → refresh → retry) and `authApi.refresh()` (`bootRefresh`,
 * used by AuthGate and AuthReturn at cold boot). Swapping the per-request
 * ADAPTER here wraps both without editing the vendored interceptors: the
 * factory's request interceptor still builds the request (base URL, CSRF), and
 * its response interceptor still sees an ordinary success or axios error.
 */
instance.interceptors.request.use((req) => {
  if (pathOf(req) !== REFRESH_PATH || (req.method ?? 'get').toUpperCase() !== 'POST') return req;
  if ((req as RefreshRetryConfig)._dinkRefreshRetry) return req;
  const transport = getAdapter(req.adapter ?? axios.defaults.adapter);
  // A sign-out after this refresh started makes its result unusable: a refresh
  // queued on the lock (or slow past the hold) must never write a token back into
  // a signed-out tab. Captured before the request is built (and before any CSRF read).
  const generation = getSignOutGeneration();
  req.adapter = async (config) => {
    const res = await refreshCoordinator.run(
      config,
      () => transport(config),
      () => resendRefresh(config),
    );
    if (getSignOutGeneration() !== generation) {
      throw new AxiosError('Signed out while the refresh was in flight', AxiosError.ERR_CANCELED, config);
    }
    return res;
  };
  return req;
});

/**
 * A FRESH refresh request through the instance, used for every attempt inside the
 * lock and for the single SUPERSEDED retry. Every refresh rotates the CSRF cookie,
 * so the CSRF token is obtained here, at send time:
 *  - with Web Locks, this function reads it itself — the `journix_csrf` cookie,
 *    else `GET /api/auth/csrf` — and sends it the way the factory's cookie-echo
 *    stage would (`X-CSRF-Token` header + `csrfToken` body echo). It runs inside
 *    the lock, so no other tab's CSRF fetch can land between this GET and the POST;
 *  - without Web Locks, the factory's cookie-echo stage sets it, as today.
 * No timeout: a slow refresh is allowed to finish. Marked to skip the coordinator, and
 * reported back in adapter terms — a 2xx response, or an axios error carrying the
 * response — against the ORIGINAL request's config.
 */
async function resendRefresh(original: InternalAxiosRequestConfig): Promise<AxiosResponse> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (original.headers.get('X-Refresh')) headers['X-Refresh'] = '1';
  const body: Record<string, string> = {};
  const retryConfig: RefreshRetryConfig = {
    headers,
    withCredentials: true,
    validateStatus: () => true,
    _dinkRefreshRetry: true,
  };
  let res: AxiosResponse;
  try {
    if (coordinatorOwnsRefreshCsrf(REFRESH_PATH)) {
      const csrf = readCookie(CSRF_COOKIE) ?? (await fetchCsrfToken());
      if (csrf) {
        headers['X-CSRF-Token'] = csrf;
        body.csrfToken = csrf;
      }
    }
    res = await instance.post(REFRESH_PATH, body, retryConfig);
  } catch {
    throw new AxiosError('Network Error', AxiosError.ERR_NETWORK, original);
  }
  const response: AxiosResponse = { ...res, config: original };
  if (res.status >= 200 && res.status < 300) return response;
  throw new AxiosError(
    `Request failed with status code ${res.status}`,
    res.status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
    original,
    undefined,
    response,
  );
}

/** `GET /api/auth/csrf` for the coordinated refresh (inside the lock). */
async function fetchCsrfToken(): Promise<string | null> {
  const res = await instance.get<{ csrfToken?: string }>(CSRF_PATH, { withCredentials: true });
  return res.data?.csrfToken ?? null;
}

/** Same cookie read as the factory's cookie-echo stage. */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const esc = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${esc}=([^;]+)`));
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

/**
 * DNKADM-0497 — iPad wake. When the tab becomes visible with an EXPIRED access
 * token, refresh (inside the cross-tab lock, via the coordinator) before any
 * dink-api call goes out: bearer-surface requests made while it is pending wait
 * for it, then carry the fresh token. A failed wake refresh signs nobody out (the
 * network is often not back yet on wake); the first call then takes the normal
 * 401 → refresh → retry path exactly as before. Bounded: calls wait for it only
 * until `wakeWaitMs` after it started; after that they go out with the token they
 * have, while the (never-aborted) wake refresh may still finish and store its token.
 */
let wakeRefresh: Promise<void> | null = null;
let wakeWaitUntil = 0;

function accessTokenExpired(): boolean {
  if (!getAccessToken()) return false;
  const expiresAt = getExpiresAt();
  if (!expiresAt) return false;
  const at = Date.parse(expiresAt);
  return Number.isFinite(at) && at <= Date.now();
}

/** Visibility handler body — exported for tests. */
export function refreshIfExpiredOnWake(): Promise<void> | null {
  if (wakeRefresh || !accessTokenExpired()) return wakeRefresh;
  wakeWaitUntil = Date.now() + refreshBounds.wakeWaitMs;
  // The wake refresh is never aborted, so it can land late. Store its result only
  // if the token is still the one it set out to replace: a sign-out meanwhile
  // (null) or a newer refresh (a different token) means the late result is
  // dropped — it must never sign a signed-out shared device back in.
  const replacing = getAccessToken();
  wakeRefresh = instance
    .post<{ accessToken?: string; expiresAt?: string | null; csrfToken?: string | null }>(
      REFRESH_PATH,
      {},
      { headers: { 'X-Refresh': '1' } },
    )
    .then(({ data }) => {
      if (data?.accessToken && getAccessToken() === replacing) {
        setAccessToken(data.accessToken, data.expiresAt ?? null);
        setCsrfToken(data.csrfToken ?? null);
      }
    })
    .catch(() => {
      /* never a sign-out — see above */
    })
    .finally(() => {
      wakeRefresh = null;
    });
  return wakeRefresh;
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void refreshIfExpiredOnWake();
  });
}

instance.interceptors.request.use(async (req) => {
  if (wakeRefresh && isBearerSurface(pathOf(req))) {
    const remaining = wakeWaitUntil - Date.now();
    if (remaining > 0) await waitAtMost(wakeRefresh, remaining);
  }
  return req;
});

/** Resolve when `promise` settles or after `ms`, whichever comes first. Never rejects. */
async function waitAtMost(promise: Promise<unknown>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      promise.catch(() => undefined),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, ms);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/** Path of an axios request config — bare path or the pathname of an absolute URL. */
function pathOf(req: { url?: string }): string {
  const u = req.url ?? '';
  if (u.startsWith('http')) {
    try {
      return new URL(u).pathname;
    } catch {
      return u;
    }
  }
  return u;
}

/** Test seam — clears the single-in-flight refresh latch (now per-instance). */
export function __resetClientState(): void {
  // The single-in-flight refresh latch lives inside the factory instance and is
  // self-clearing (it nulls in a `.finally`), so it is always idle between
  // awaited tests. This seam is retained for source/test compatibility with the
  // many wrapper suites that call it in their setup/teardown.
  // DNKADM-0497: also forget any other tab's refresh result and a pending wake refresh.
  refreshCoordinator.clear();
  wakeRefresh = null;
  wakeWaitUntil = 0;
  refreshBounds = { ...DEFAULT_REFRESH_BOUNDS };
}

export interface RequestOptions {
  json?: unknown;
  /**
   * A `multipart/form-data` body (DNKADM-0048 — the repo's first multipart
   * upload). When set, the body is sent verbatim and we deliberately DO NOT set
   * `Content-Type`, so the browser/axios stamps `multipart/form-data` with the
   * correct boundary. Mutually exclusive with `json` (a `FormData` body is never
   * JSON). The bearer `/admin/**` treatment is unchanged (bearer only, no CSRF,
   * no cookies) — only the Content-Type handling differs.
   */
  form?: FormData;
  query?: Record<string, string | number | undefined>;
  headers?: Record<string, string>;
  /**
   * Response body type override (DNKADM-0304 U4 — the authed contact-photo
   * bytes read). `'blob'` makes axios resolve `res.data` as a `Blob`; omitted
   * keeps the default JSON handling. Config-level passthrough only — the
   * vendored interceptors are untouched.
   */
  responseType?: 'blob';
  /**
   * Cancels the request when aborted (DNKADM-0460 — Ask Dink's Stop button). Passed
   * straight to axios's `signal`; config-level only, the vendored interceptors are
   * untouched. An aborted call rejects like a transport failure (`NETWORK_ERROR`), so
   * the caller must already know it aborted and ignore the rejection.
   */
  signal?: AbortSignal;
  /** Internal: this call IS the refresh; never refresh-on-401 from inside it. */
  _isRefresh?: boolean;
  /** Internal: this call is the single post-refresh retry; never refresh again. */
  _isRetry?: boolean;
}

export async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  // The factory emits Accept + X-Request-ID; this wrapper adds dink-admin's
  // extras the factory does not: caller headers, the conditional Content-Type
  // (only with a JSON body), and the DNKADM-0034 `X-Session-Mode` admin-mutation
  // header.
  const headers: Record<string, string> = { ...options.headers };
  if (options.json !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  // A `FormData` body (DNKADM-0048 multipart upload) must NOT carry an explicit
  // Content-Type — the browser sets `multipart/form-data; boundary=…` itself. A
  // caller-spread `Content-Type` (e.g. a stray JSON header) is dropped so the
  // boundary is never clobbered.
  if (options.form !== undefined) {
    delete headers['Content-Type'];
  }

  // Audit-attribution ONLY — NOT a security control (label, not lock). On
  // mutating `/admin/**` (and `/v2/admin/**`, DNKADM-0049) calls, stamp the
  // admin's current session mode (`front-desk` | `open`, read at send time from
  // the existing Front Desk flag) so dink-api can attribute the audit entry as
  // "(Front Desk)/(Open)". dink-api does NOT verify or authorize on this; nothing
  // on the SPA reads it back to gate anything (DNKADM-0034 / DNKAPI-0088).
  // Excluded: GET reads, the auth surface, and the refresh/retry internals. A
  // caller-supplied `X-Session-Mode` (already spread in from `options.headers`)
  // wins over the central injection.
  if (
    method !== 'GET' &&
    (isAdminSurface(path) || isV2AdminSurface(path)) &&
    !options._isRefresh &&
    !options._isRetry &&
    headers['X-Session-Mode'] === undefined
  ) {
    headers['X-Session-Mode'] = readFrontDeskView() ? 'front-desk' : 'open';
  }

  const config: AxiosRequestConfig = {
    method,
    // Pass the bare path as the axios `url`; the factory's request interceptor
    // sets `baseURL` from `resolveBaseUrl(path)` so the surface predicates and
    // two-base routing operate on the clean path (not a query-laden URL).
    url: path,
    headers,
    // `credentials:'include'` ONLY on the saas-auth cookie surface. The factory
    // instance defaults `withCredentials: true` (correct for its internal
    // refresh/csrf auth-surface calls); we scope it per-request so dink-api
    // bearer calls send NO cookies — the Option-A invariant.
    withCredentials: isAuthSurface(path),
  };
  if (options.json !== undefined) {
    // Only attach a body (and thus Content-Type) when a JSON payload is present.
    config.data = options.json;
  } else if (options.form !== undefined) {
    // A multipart body: pass the FormData through; axios/the browser sets the
    // `multipart/form-data` Content-Type + boundary (we left it unset above).
    config.data = options.form;
  }
  if (options.query) {
    config.params = options.query;
  }
  if (options.responseType !== undefined) {
    config.responseType = options.responseType;
  }
  if (options.signal !== undefined) {
    config.signal = options.signal;
  }

  let data: T;
  try {
    const res = await instance.request<T>(config);
    data = res.status === 204 ? (undefined as T) : res.data;
  } catch (err) {
    throw toDinkApiError(err);
  }

  // Normalize an empty body to `null` (matching the prior fetch primitive, which
  // returned `null` for a zero-length non-204 body); 204 already returned above.
  return (data === '' ? (null as T) : data);
}

/**
 * Convert a thrown factory `ApiError` into dink-admin's `ApiError`, preserving
 * the existing code mapping: `NETWORK_ERROR` on transport failure (status 0),
 * `SESSION_EXPIRED` on a dead session, otherwise `statusToCode(status)` with the
 * server's diagnostic message extracted from the body (top-level `{ message }`
 * or `{ error: { message } }`) — the same shape every wrapper test asserts.
 */
function toDinkApiError(err: unknown): ApiError {
  if (err instanceof FactoryApiError) {
    if (err.code === 'NETWORK_ERROR') {
      return new ApiError('NETWORK_ERROR', 0, initFrom(err.requestId));
    }
    if (err.code === 'SESSION_EXPIRED') {
      return new ApiError('SESSION_EXPIRED', 401, initFrom(err.requestId));
    }
    // The factory carries the raw response body in `details.raw` (it only omits
    // it when the backend supplied an `error.code`, which dink-api/saas-auth do
    // not on these surfaces). Re-derive the message dink-admin's way.
    return apiErrorFromBody(err.status, err.details.raw, err.requestId, err.details.message);
  }
  // An ApiError already in dink-admin's shape (or any other throw) — pass an
  // ApiError through untouched, else wrap as UNKNOWN.
  if (err instanceof ApiError) return err;
  return new ApiError('UNKNOWN', 0, {});
}

/** Body keys lifted to a dedicated place (or unsafe to copy); every other key is kept in `details`. */
const ENVELOPE_KEYS = new Set([
  '__proto__',
  'message',
  'code',
  'error',
  'requestId',
  'retryable',
  'status',
]);

/**
 * Build an `ApiError` from a non-2xx response body. dink-api / saas-auth error
 * bodies may carry `{ message }`, `{ error: { message } }`, or a non-JSON
 * string; we map the status to a code and preserve any message for diagnostics.
 *
 * The v2 (Project Independence) envelope is `{ code, message, status }`
 * (DNKADM-0049, handoff §4.2). The plan-limit rejection (DNKAPI-0139) instead
 * keys the fine-grained code under a top-level string **`error`**
 * (`{ error: 'PLAN_LIMIT_REACHED', axis, limit, current, plan }`). So lift the
 * discriminator from **`code` OR a string `error`** — preferring `code` for the
 * standard envelope, falling back to a string `error` — into `details.code`, so
 * it is reachable via `v2ErrorCode()` (`./v2Errors`). (A `{ error: { message } }`
 * OBJECT is not a discriminator; only its nested message is read.) Additionally
 * preserve the plan-limit payload (`axis`/`limit`/`current`/`plan`) under
 * `details.planLimit` so `parsePlanLimit()` can drive the upgrade popup; the
 * fields are copied verbatim (present keys only) and coerced defensively at parse
 * time. Likewise preserve the DNKAPI-0542 `conflicts` array (the `409 STAFF_CONFLICT`
 * envelope's additive key naming the booking(s) in the way) under
 * `details.conflicts`, verbatim, for `parseStaffConflict()` (`./reservations`) to
 * coerce, and the DNKAPI-0546 `staffChange` OBJECT (the `409 STAFF_WILL_CHANGE`
 * envelope's additive key naming the outgoing and incoming coach) under
 * `details.staffChange`, verbatim, for `parseStaffChange()` (same module). Only
 * present keys are assigned (never `undefined`), satisfying
 * `exactOptionalPropertyTypes`.
 *
 * DNKAPI-0549 (DNKADM-0396) adds two more additive keys. Since DNKADM-0474 this function
 * is a PASSTHROUGH: every body key other than {@link ENVELOPE_KEYS} is kept under `details`
 * (the old hand-kept allow-list dropped a new payload silently — the DNKADM-0393 U5 lesson,
 * where `staffChange` went missing that way). The typed picks below still guard the shape
 * of the arrays/objects their parsers expect. The two keys:
 *  - `overrideBlocks` — the array naming EVERY reason one admin override was refused
 *    (`{ code, message, acknowledgeField, resourceConflicts }` per entry), preserved
 *    under `details.overrideBlocks` for `parseOverrideRefusal()` (`./reservations`);
 *  - `resourceConflicts` — the TOP-LEVEL array naming the booking(s) already holding the
 *    bay on a `RESOURCE_CONFLICT`, preserved under `details.resourceConflicts`.
 *
 * `resourceConflicts` is a **separate key from `conflicts` on purpose** (dink-api
 * `V2ExceptionHandler.handleOverrideBlocked` leaves `conflicts` null): the entries have
 * the identical shape, but `conflicts` present still means the code is `STAFF_CONFLICT`.
 * Do not merge them here.
 */
function apiErrorFromBody(
  status: number,
  body: unknown,
  requestId: string | undefined,
  fallbackMessage?: string,
): ApiError {
  const code: ErrorCode = statusToCode(status);
  let message: string | undefined = fallbackMessage;
  let envelopeCode: string | undefined;
  let envelopeRequestId: string | undefined;
  let retryable: boolean | undefined;
  let planLimit: Record<string, unknown> | undefined;
  let conflicts: unknown[] | undefined;
  let staffChange: Record<string, unknown> | undefined;
  let resourceConflicts: unknown[] | undefined;
  let overrideBlocks: unknown[] | undefined;
  if (body && typeof body === 'object') {
    const b = body as {
      code?: unknown;
      message?: unknown;
      error?: unknown;
      requestId?: unknown;
      retryable?: unknown;
      conflicts?: unknown;
      staffChange?: unknown;
      resourceConflicts?: unknown;
      overrideBlocks?: unknown;
    };
    if (typeof b.message === 'string') message = b.message;
    else if (
      b.error &&
      typeof b.error === 'object' &&
      typeof (b.error as { message?: unknown }).message === 'string'
    ) {
      message = (b.error as { message: string }).message;
    }
    // Discriminator: prefer the standard v2 envelope `code`; fall back to a
    // top-level string `error` (the plan-limit shape, DNKAPI-0139). An `error`
    // OBJECT carries only a message (handled above), never the discriminator.
    if (typeof b.code === 'string') envelopeCode = b.code;
    else if (typeof b.error === 'string') envelopeCode = b.error;
    if (typeof b.requestId === 'string') envelopeRequestId = b.requestId;
    if (typeof b.retryable === 'boolean') retryable = b.retryable;
    // Preserve any plan-limit payload (present keys only); parsed defensively in
    // `parsePlanLimit`.
    planLimit = pickPlanLimitFields(b);
    // Preserve the DNKAPI-0542 conflicts array verbatim (an array only — anything
    // else is dropped, and the entries are coerced in `parseStaffConflict`).
    if (Array.isArray(b.conflicts)) conflicts = b.conflicts;
    // Preserve the DNKAPI-0546 staffChange object verbatim (a non-array OBJECT only —
    // anything else, including an explicit null, is dropped; the members are coerced in
    // `parseStaffChange`). `staffChange` is ABSENT on every other error body, so the key
    // exists here exactly when the server sent the coach-change payload.
    if (b.staffChange !== null && typeof b.staffChange === 'object' && !Array.isArray(b.staffChange)) {
      staffChange = b.staffChange as Record<string, unknown>;
    }
    // Preserve the DNKAPI-0549 arrays verbatim (arrays only — anything else, an explicit
    // null included, is dropped; the entries are coerced in `parseOverrideRefusal`). Kept
    // as two distinct keys: `resourceConflicts` is the bay's blocking bookings and never
    // aliases `conflicts`, which stays the coach's alone.
    if (Array.isArray(b.resourceConflicts)) resourceConflicts = b.resourceConflicts;
    if (Array.isArray(b.overrideBlocks)) overrideBlocks = b.overrideBlocks;
  } else if (typeof body === 'string' && body.length > 0) {
    message = body;
  }
  // DNKADM-0474 (I-36): every structured key the server sent is kept under `details`, so a new
  // payload (`declineReason`, `currentStatus`, `key`, the next one) is never dropped by a list
  // nobody updated. Only the keys lifted to a dedicated place (`message`, `code`/`error`,
  // `requestId`, `retryable`) stay out; the typed picks below overwrite their raw copies.
  const extras: Record<string, unknown> = {};
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    for (const [key, value] of Object.entries(body as Record<string, unknown>)) {
      if (ENVELOPE_KEYS.has(key) || value === undefined) continue;
      extras[key] = value;
    }
  }
  let details: Record<string, unknown> | undefined;
  if (
    message !== undefined ||
    envelopeCode !== undefined ||
    planLimit !== undefined ||
    conflicts !== undefined ||
    staffChange !== undefined ||
    resourceConflicts !== undefined ||
    overrideBlocks !== undefined ||
    Object.keys(extras).length > 0
  ) {
    details = { ...extras };
    if (message !== undefined) details['message'] = message;
    if (envelopeCode !== undefined) details['code'] = envelopeCode;
    if (planLimit !== undefined) details['planLimit'] = planLimit;
    // The typed arrays/objects keep their shape guard: a wrong-typed value is dropped, not
    // handed to a parser that expects an array/object.
    delete details['conflicts'];
    delete details['staffChange'];
    delete details['resourceConflicts'];
    delete details['overrideBlocks'];
    if (conflicts !== undefined) details['conflicts'] = conflicts;
    if (staffChange !== undefined) details['staffChange'] = staffChange;
    if (resourceConflicts !== undefined) details['resourceConflicts'] = resourceConflicts;
    if (overrideBlocks !== undefined) details['overrideBlocks'] = overrideBlocks;
  }
  return new ApiError(code, status, initFrom(envelopeRequestId ?? requestId, details, retryable));
}

/**
 * Copy the plan-limit fields (`axis`/`limit`/`current`/`plan`, DNKAPI-0139) off a
 * response body, preserving only the keys that are present (raw values — the
 * defensive coercion to `PlanLimitInfo` happens in `parsePlanLimit`). Returns
 * `undefined` when none are present so no empty object is stored. Never throws on
 * a missing/odd-typed field.
 */
function pickPlanLimitFields(b: Record<string, unknown>): Record<string, unknown> | undefined {
  const out: Record<string, unknown> = {};
  for (const key of ['axis', 'limit', 'current', 'plan'] as const) {
    if (key in b && b[key] !== undefined) out[key] = b[key];
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Build an `ApiErrorInit` without setting `undefined` properties, satisfying
 * dink-admin's `exactOptionalPropertyTypes`. Only present keys are assigned.
 */
function initFrom(
  requestId: string | undefined,
  details?: Record<string, unknown>,
  retryable?: boolean,
): { requestId?: string; retryable?: boolean; details?: Record<string, unknown> } {
  const init: { requestId?: string; retryable?: boolean; details?: Record<string, unknown> } = {};
  if (requestId !== undefined) init.requestId = requestId;
  if (retryable !== undefined) init.retryable = retryable;
  if (details !== undefined) init.details = details;
  return init;
}
